/**
 * useEffectiveRole — single source of truth for "what role does the UI
 * think you are RIGHT NOW".
 *
 * Strict hierarchy: developer > institution_admin / teacher > student.
 * When a developer or faculty uses a role-switch route, the *effective*
 * role drops down the hierarchy so previewed UI honestly reflects what
 * the target audience would see.
 *
 *   URL prefix                Effective role
 *   ───────────────────────   ───────────────────────────────────
 *   /preview-as-student/...   'student'   (regardless of real role)
 *   /preview-as-faculty/...   'teacher'   (developer → faculty downgrade)
 *   anything else             user.role   (the authenticated real role)
 *
 * Returns helper booleans so most call sites need a single import:
 *
 *   const { isFaculty, isDeveloper, isStudent, effectiveRole,
 *           isStudentPreview, isFacultyPreview } = useEffectiveRole();
 */
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const FACULTY_ROLES = ['teacher', 'institution_admin', 'developer'];

export default function useEffectiveRole() {
  const { user } = useAuth();
  const location = useLocation();
  const path = location.pathname || '';

  const isStudentPreview = path.startsWith('/preview-as-student');
  const isFacultyPreview = path.startsWith('/preview-as-faculty');

  const realRole = user?.role || null;
  let effectiveRole = realRole;
  if (isStudentPreview) effectiveRole = 'student';
  else if (isFacultyPreview && realRole === 'developer') effectiveRole = 'teacher';

  return {
    user,
    realRole,
    effectiveRole,
    isStudentPreview,
    isFacultyPreview,
    isStudent:    effectiveRole === 'student',
    isTeacher:    effectiveRole === 'teacher',
    isAdmin:      effectiveRole === 'institution_admin',
    isDeveloper:  effectiveRole === 'developer',
    isFaculty:    FACULTY_ROLES.includes(effectiveRole || ''),
  };
}
